'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function WeightCalculator() {
  const t = useTranslations("tools.weightCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    height: 170,
    heightUnit: 'cm',
    weight: 70,
    weightUnit: 'kg',
    age: 30,
    gender: 'male',
    activityLevel: 'moderate',
    bodyFrame: 'medium'
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, margin: '0.5rem 0 0 0' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: {
      textAlign: 'center' as const,
      marginBottom: '2rem'
    },
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, margin: '0 0 0.5rem 0' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    toolSubtitle: getResponsiveStyle(
      { color: colors.textSecondary, margin: 0 },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    formGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    formGroup: {
      marginBottom: '1.5rem'
    },
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', color: colors.textPrimary, fontWeight: 600 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
        fontSize: '1rem'
      },
      { padding: '0.75rem 1rem' },
      { padding: '0.875rem 1.25rem' },
      { padding: '1rem 1.25rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
        fontSize: '1rem',
        background: 'white',
        cursor: 'pointer'
      },
      { padding: '0.75rem 1rem' },
      { padding: '0.875rem 1.25rem' },
      { padding: '1rem 1.25rem' }
    ),
    inputGroup: {
      display: 'flex',
      gap: '0.5rem'
    },
    inputWithUnit: {
      flex: 1
    },
    unitSelect: getResponsiveStyle(
      {
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        background: 'white',
        cursor: 'pointer',
        fontSize: '1rem'
      },
      { padding: '0.75rem', width: '80px' },
      { padding: '0.875rem', width: '90px' },
      { padding: '1rem', width: '100px' }
    ),
    buttonGroup: getResponsiveStyle(
      { display: 'flex', gap: '1rem', justifyContent: 'center' },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      { flexDirection: 'row' as const }
    ),
    calculateButton: getResponsiveStyle(
      {
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white'
      },
      { padding: '0.875rem 2rem', fontSize: '1rem', width: '100%' },
      { padding: '0.875rem 2.5rem', fontSize: '1rem', width: 'auto' },
      { padding: '1rem 3rem', fontSize: '1.125rem', width: 'auto' }
    ),
    clearButton: getResponsiveStyle(
      {
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        background: '#f1f5f9',
        color: '#475569'
      },
      { padding: '0.875rem 2rem', fontSize: '1rem', width: '100%' },
      { padding: '0.875rem 2.5rem', fontSize: '1rem', width: 'auto' },
      { padding: '1rem 3rem', fontSize: '1.125rem', width: 'auto' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { color: colors.textPrimary, margin: '0 0 1.5rem 0', fontWeight: 700, textAlign: 'center' as const },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' }
    ),
    resultItem: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '0.75rem',
        borderLeft: '4px solid #2563eb',
        marginBottom: '1rem'
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    resultLabel: getResponsiveStyle(
      { display: 'block', color: colors.textSecondary, marginBottom: '0.5rem', fontWeight: 500 },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' }
    ),
    resultValue: getResponsiveStyle(
      { display: 'block', color: colors.textPrimary, fontWeight: 700 },
      { fontSize: '1.5rem' },
      { fontSize: '1.65rem' },
      { fontSize: '1.75rem' }
    ),
    bmiCategory: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 600,
      marginTop: '0.5rem'
    },
    rangeTable: {
      marginTop: '1.5rem',
      overflowX: 'auto' as const
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const
    },
    th: getResponsiveStyle(
      {
        padding: '0.75rem',
        textAlign: 'left' as const,
        borderBottom: '1px solid #e2e8f0',
        background: '#f8fafc',
        color: '#475569',
        fontWeight: 600
      },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' }
    ),
    td: getResponsiveStyle(
      {
        padding: '0.75rem',
        color: colors.textPrimary,
        borderBottom: '1px solid #e2e8f0'
      },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    strong: {
      color: colors.textPrimary,
      fontWeight: 600
    },
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { color: colors.textPrimary, fontWeight: 600, marginBottom: '0.5rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.1875rem' }
    ),
    reference: {
      marginBottom: '0.75rem'
    },
    link: {
      color: colors.primary,
      textDecoration: 'none',
      transition: 'color 0.3s ease'
    }
  };

  const convertToKg = (value: number, unit: string): number => {
    switch (unit) {
      case 'kg': return value;
      case 'lb': return value * 0.453592;
      case 'st': return value * 6.35029;
      default: return value;
    }
  };

  const convertToCm = (value: number, unit: string): number => {
    switch (unit) {
      case 'cm': return value;
      case 'ft': return value * 30.48;
      case 'in': return value * 2.54;
      case 'm': return value * 100;
      default: return value;
    }
  };

  const calculateBMI = (weightKg: number, heightCm: number): number => {
    const heightM = heightCm / 100;
    return weightKg / (heightM * heightM);
  };

  const getBMICategory = (bmi: number): { category: string; color: string } => {
    if (bmi < 18.5) return { category: 'Underweight', color: '#3b82f6' };
    if (bmi < 25) return { category: 'Normal weight', color: '#10b981' };
    if (bmi < 30) return { category: 'Overweight', color: '#f59e0b' };
    return { category: 'Obesity', color: '#ef4444' };
  };

  const calculateIdealWeight = (heightCm: number, gender: string, frame: string): any => {
    const heightInches = heightCm / 2.54;

    // Devine formula
    let devine = 0;
    if (gender === 'male') {
      devine = 50 + 2.3 * (heightInches - 60);
    } else {
      devine = 45.5 + 2.3 * (heightInches - 60);
    }

    // Robinson formula
    let robinson = 0;
    if (gender === 'male') {
      robinson = 52 + 1.9 * (heightInches - 60);
    } else {
      robinson = 49 + 1.7 * (heightInches - 60);
    }

    // Miller formula
    let miller = 0;
    if (gender === 'male') {
      miller = 56.2 + 1.41 * (heightInches - 60);
    } else {
      miller = 53.1 + 1.36 * (heightInches - 60);
    }

    // Hamwi formula
    let hamwi = 0;
    if (gender === 'male') {
      hamwi = 48 + 2.7 * (heightInches - 60);
    } else {
      hamwi = 45.5 + 2.2 * (heightInches - 60);
    }

    // Average
    const average = (devine + robinson + miller + hamwi) / 4;

    // Adjust for frame
    let frameAdjustment = 1;
    if (frame === 'small') frameAdjustment = 0.9;
    if (frame === 'large') frameAdjustment = 1.1;

    return {
      devine: devine * frameAdjustment,
      robinson: robinson * frameAdjustment,
      miller: miller * frameAdjustment,
      hamwi: hamwi * frameAdjustment,
      average: average * frameAdjustment,
      healthyMin: average * frameAdjustment * 0.9,
      healthyMax: average * frameAdjustment * 1.1
    };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const weightKg = convertToKg(inputs.weight, inputs.weightUnit);
    const heightCm = convertToCm(inputs.height, inputs.heightUnit);

    const bmi = calculateBMI(weightKg, heightCm);
    const bmiInfo = getBMICategory(bmi);
    const idealWeights = calculateIdealWeight(heightCm, inputs.gender, inputs.bodyFrame);

    const weightDifference = weightKg - idealWeights.average;

    setResults({
      currentWeight: weightKg,
      currentBMI: bmi,
      bmiCategory: bmiInfo.category,
      bmiColor: bmiInfo.color,
      idealWeights,
      weightDifference,
      healthyRange: {
        min: idealWeights.healthyMin,
        max: idealWeights.healthyMax
      }
    });
  };

  const handleClear = () => {
    setInputs({
      height: 170,
      heightUnit: 'cm',
      weight: 70,
      weightUnit: 'kg',
      age: 30,
      gender: 'male',
      activityLevel: 'moderate',
      bodyFrame: 'medium'
    });
    setResults(null);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t("toolTitle")}</h2>
            <p style={styles.toolSubtitle}>{t("toolSubtitle")}</p>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t("heightLabel")}</label>
                <div style={styles.inputGroup}>
                  <input
                    type="number"
                    step="any"
                    value={inputs.height}
                    onChange={(e) => setInputs({...inputs, height: parseFloat(e.target.value) || 0})}
                    style={{...styles.input, ...styles.inputWithUnit}}
                    required
                  />
                  <select
                    value={inputs.heightUnit}
                    onChange={(e) => setInputs({...inputs, heightUnit: e.target.value})}
                    style={styles.unitSelect}
                  >
                    <option value="cm">cm</option>
                    <option value="m">m</option>
                    <option value="ft">ft</option>
                    <option value="in">in</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("weightLabel")}</label>
                <div style={styles.inputGroup}>
                  <input
                    type="number"
                    step="any"
                    value={inputs.weight}
                    onChange={(e) => setInputs({...inputs, weight: parseFloat(e.target.value) || 0})}
                    style={{...styles.input, ...styles.inputWithUnit}}
                    required
                  />
                  <select
                    value={inputs.weightUnit}
                    onChange={(e) => setInputs({...inputs, weightUnit: e.target.value})}
                    style={styles.unitSelect}
                  >
                    <option value="kg">kg</option>
                    <option value="lb">lb</option>
                    <option value="st">st</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("ageLabel")}</label>
                <input
                  type="number"
                  value={inputs.age}
                  onChange={(e) => setInputs({...inputs, age: parseInt(e.target.value) || 0})}
                  style={styles.input}
                  min="1"
                  max="120"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("genderLabel")}</label>
                <select
                  value={inputs.gender}
                  onChange={(e) => setInputs({...inputs, gender: e.target.value})}
                  style={styles.select}
                >
                  <option value="male">{t("genderMale")}</option>
                  <option value="female">{t("genderFemale")}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("activityLabel")}</label>
                <select
                  value={inputs.activityLevel}
                  onChange={(e) => setInputs({...inputs, activityLevel: e.target.value})}
                  style={styles.select}
                >
                  <option value="sedentary">{t("activitySedentary")}</option>
                  <option value="light">{t("activityLight")}</option>
                  <option value="moderate">{t("activityModerate")}</option>
                  <option value="active">{t("activityActive")}</option>
                  <option value="veryActive">{t("activityVeryActive")}</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t("frameLabel")}</label>
                <select
                  value={inputs.bodyFrame}
                  onChange={(e) => setInputs({...inputs, bodyFrame: e.target.value})}
                  style={styles.select}
                >
                  <option value="small">{t("frameSmall")}</option>
                  <option value="medium">{t("frameMedium")}</option>
                  <option value="large">{t("frameLarge")}</option>
                </select>
              </div>
            </div>

            <div style={styles.buttonGroup}>
              <button type="submit" style={styles.calculateButton}>
                {t("calculateButton")}
              </button>
              <button type="button" onClick={handleClear} style={styles.clearButton}>
                {t("clearButton")}
              </button>
            </div>
          </form>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>{t("resultsTitle")}</h3>

              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t("currentBMILabel")}</span>
                <span style={styles.resultValue}>{results.currentBMI.toFixed(1)}</span>
                <div style={{...styles.bmiCategory, background: results.bmiColor, color: 'white'}}>
                  {results.bmiCategory}
                </div>
              </div>

              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t("idealWeightLabel")}</span>
                <span style={styles.resultValue}>
                  {results.idealWeights.average.toFixed(1)} kg
                </span>
              </div>

              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t("healthyRangeLabel")}</span>
                <span style={styles.resultValue}>
                  {results.healthyRange.min.toFixed(1)} - {results.healthyRange.max.toFixed(1)} kg
                </span>
              </div>

              <div style={styles.resultItem}>
                <span style={styles.resultLabel}>{t("weightDifferenceLabel")}</span>
                <span style={styles.resultValue}>
                  {results.weightDifference > 0 ? '+' : ''}{results.weightDifference.toFixed(1)} kg
                </span>
              </div>

              <div style={styles.rangeTable}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>{t("formulaColumn")}</th>
                      <th style={styles.th}>{t("idealWeightColumn")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={styles.td}>{t("devineFormula")}</td>
                      <td style={styles.td}><strong>{results.idealWeights.devine.toFixed(1)} kg</strong></td>
                    </tr>
                    <tr>
                      <td style={styles.td}>{t("robinsonFormula")}</td>
                      <td style={styles.td}><strong>{results.idealWeights.robinson.toFixed(1)} kg</strong></td>
                    </tr>
                    <tr>
                      <td style={styles.td}>{t("millerFormula")}</td>
                      <td style={styles.td}><strong>{results.idealWeights.miller.toFixed(1)} kg</strong></td>
                    </tr>
                    <tr>
                      <td style={styles.td}>{t("hamwiFormula")}</td>
                      <td style={styles.td}><strong>{results.idealWeights.hamwi.toFixed(1)} kg</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("howToStep1")}</li>
          <li style={styles.listItem}>{t("howToStep2")}</li>
          <li style={styles.listItem}>{t("howToStep3")}</li>
          <li style={styles.listItem}>{t("howToStep4")}</li>
          <li style={styles.listItem}>{t("howToStep5")}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <p style={styles.paragraph}>{t("insightsParagraph1")}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t("bmiCategoriesTitle")}
        </h3>
        <ul style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t("bmiUnderweight")}</span> {t("bmiUnderweightDesc")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("bmiNormal")}</span> {t("bmiNormalDesc")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("bmiOverweight")}</span> {t("bmiOverweightDesc")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("bmiObese")}</span> {t("bmiObeseDesc")}</li>
        </ul>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t("formulasTitle")}
        </h3>
        <p style={styles.paragraph}>{t("formulasParagraph")}</p>
      </section>

      {/* Best Practices */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("bestPracticesTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t("practice1Title")}</span> {t("practice1Desc")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("practice2Title")}</span> {t("practice2Desc")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("practice3Title")}</span> {t("practice3Desc")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("practice4Title")}</span> {t("practice4Desc")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("practice5Title")}</span> {t("practice5Desc")}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t("practice6Title")}</span> {t("practice6Desc")}</li>
        </ul>
      </section>

      {/* Health Considerations */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("healthTitle")}</h2>
        <p style={styles.paragraph}>{t("healthParagraph1")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("healthBenefit1")}</li>
          <li style={styles.listItem}>{t("healthBenefit2")}</li>
          <li style={styles.listItem}>{t("healthBenefit3")}</li>
          <li style={styles.listItem}>{t("healthBenefit4")}</li>
          <li style={styles.listItem}>{t("healthBenefit5")}</li>
        </ul>
        <p style={styles.paragraph}>{t("healthParagraph2")}</p>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq1Question")}</h3>
          <p style={styles.paragraph}>{t("faq1Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq2Question")}</h3>
          <p style={styles.paragraph}>{t("faq2Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq3Question")}</h3>
          <p style={styles.paragraph}>{t("faq3Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq4Question")}</h3>
          <p style={styles.paragraph}>{t("faq4Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq5Question")}</h3>
          <p style={styles.paragraph}>{t("faq5Answer")}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faq6Question")}</h3>
          <p style={styles.paragraph}>{t("faq6Answer")}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <div style={styles.reference}>
          <a href="https://www.calculator.net/ideal-weight-calculator.html" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t("reference1")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.aarp.org/membership/benefits/health/bmi-calculator/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t("reference2")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.cancer.org/cancer/risk-prevention/diet-physical-activity/body-weight-and-cancer-risk/body-mass-index-bmi-calculator.html" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t("reference3")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://prognohealth.com/calculator/weight-calculator/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t("reference4")}
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://mevolife.com/health-calculators/ideal-weight-calculator" target="_blank" rel="noopener noreferrer" style={styles.link}>
            {t("reference5")}
          </a>
        </div>
      </section>
    </div>
  );
}